import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import { useEffect, useState } from 'react';
import type { EditorContext, WorkItemSummary } from '../../types';
import { SuggestionList, createSuggestionRenderer } from '../suggestion';

/**
 * `#123` — Azure DevOps work item reference. ADO wiki renders these as live
 * links, and (for project wikis) records a link on the work item back to the
 * page, which is what gives you traceability from requirement to work.
 */

export interface WorkItemRefOptions {
  ctx: EditorContext | null;
}

const cache = new Map<number, WorkItemSummary | null>();
const inflight = new Map<number, Promise<WorkItemSummary | null>>();

function lookup(ctx: EditorContext | null, id: number): Promise<WorkItemSummary | null> {
  if (!ctx) return Promise.resolve(null);
  if (cache.has(id)) return Promise.resolve(cache.get(id)!);
  let p = inflight.get(id);
  if (!p) {
    p = ctx.api
      .getWorkItems([id])
      .then(items => items[0] ?? null)
      .catch(() => null)
      .then(item => {
        cache.set(id, item);
        inflight.delete(id);
        return item;
      });
    inflight.set(id, p);
  }
  return p;
}

export function primeWorkItemCache(items: WorkItemSummary[]) {
  for (const i of items) cache.set(i.id, i);
}

function WorkItemChip({ node, extension, selected }: NodeViewProps) {
  const ctx = (extension.options as WorkItemRefOptions).ctx;
  const id = Number(node.attrs.id);
  const [item, setItem] = useState<WorkItemSummary | null | undefined>(cache.get(id));

  useEffect(() => {
    let live = true;
    lookup(ctx, id).then(i => live && setItem(i));
    return () => {
      live = false;
    };
  }, [ctx, id]);

  const href = ctx ? `https://dev.azure.com/${ctx.adoOrg}/${encodeURIComponent(item?.project ?? ctx.project)}/_workitems/edit/${id}` : undefined;
  const typeClass = item ? `wi-type-${item.type.toLowerCase().replace(/\s+/g, '-')}` : '';

  return (
    <NodeViewWrapper as="span" className={`wi-ref ${typeClass}${selected ? ' is-selected' : ''}`} data-type="work-item" data-id={id} title={item ? `${item.type} · ${item.state}${item.assignedTo ? ` · ${item.assignedTo}` : ''}` : undefined}>
      <a href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} contentEditable={false}>
        <span className="wi-id">#{id}</span>
        {item === undefined && <span className="wi-title wi-loading">…</span>}
        {item === null && <span className="wi-title wi-missing">not found</span>}
        {item && <span className="wi-title">{item.title}</span>}
      </a>
    </NodeViewWrapper>
  );
}

function WorkItemSuggestionItem(item: WorkItemSummary) {
  return (
    <>
      <span className="suggestion-id">#{item.id}</span>
      <span className="suggestion-title">{item.title}</span>
      <span className="suggestion-meta">{item.type} · {item.state} · {item.project}</span>
    </>
  );
}

export const WorkItemRef = Node.create<WorkItemRefOptions>({
  name: 'workItemRef',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { ctx: null };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: el => Number(el.getAttribute('data-id')),
        renderHTML: attrs => ({ 'data-id': attrs.id }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="work-item"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-type': 'work-item', class: 'wi-ref' }, HTMLAttributes), `#${node.attrs.id}`];
  },

  renderText({ node }) {
    return `#${node.attrs.id}`;
  },

  // ---- markdown ----
  markdownTokenName: 'workItemRef',
  markdownTokenizer: {
    name: 'workItemRef',
    level: 'inline',
    start(src) {
      // "#" followed by a digit, not glued to a word character or an HTML entity (&#39;).
      const m = /(^|[^\w&])#\d/.exec(src);
      return m ? m.index + m[1].length : -1;
    },
    tokenize(src, tokens) {
      const m = /^#(\d+)(?![\w])/.exec(src);
      if (!m) return undefined;
      // marked probes `start` on src.slice(1), so "C#5" looks like "#5" to it; the
      // previously emitted token tells us what really precedes the "#".
      const prev = tokens.length ? String(tokens[tokens.length - 1].raw ?? '') : '';
      if (prev && /[\w&]$/.test(prev)) return undefined;
      return { type: 'workItemRef', raw: m[0], id: Number(m[1]) };
    },
  },
  parseMarkdown(token, h) {
    return h.createNode('workItemRef', { id: token.id });
  },
  renderMarkdown(node) {
    return `#${node.attrs?.id}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(WorkItemChip, { as: 'span' });
  },

  addProseMirrorPlugins() {
    const ctx = this.options.ctx;
    return [
      Suggestion<WorkItemSummary>({
        editor: this.editor,
        pluginKey: new PluginKey('workItemSuggestion'),
        char: '#',
        allowSpaces: true,
        allowedPrefixes: [' ', '(', '[', ',', ';', ':'],
        items: async ({ query }) => {
          if (!ctx) return [];
          try {
            const items = await ctx.api.searchWorkItems(query, ctx.project);
            primeWorkItemCache(items);
            return items;
          } catch {
            return [];
          }
        },
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range, [{ type: 'workItemRef', attrs: { id: props.id } }, { type: 'text', text: ' ' }])
            .run();
        },
        render: createSuggestionRenderer<WorkItemSummary>(SuggestionList, { renderItem: WorkItemSuggestionItem, emptyText: 'No work items match' }),
      }),
    ];
  },
});
